import axios from 'axios'
import { ENV } from '../Utils/_helpers/EnvHelper'

const API_URL = ENV().REACT_APP_API_URL

export interface BulkOperationResponse {
  status: boolean
  message: string
  data?: any
}

export interface TeamMember {
  _id: string
  name: string
  email: string
  avatar?: string
  assignedContacts?: string[]
}

export interface BulkOperationPayload {
  conversationIds: string[]
}

export interface BulkAssignPayload extends BulkOperationPayload {
  assigneeId: string
}

export interface BulkTagPayload extends BulkOperationPayload {
  tagIds: string[]
}

export interface BulkFlagPayload extends BulkOperationPayload {
  flagStatus?: boolean
  isFlagged?: boolean
}

class BulkOperationsAPI {
  private getAuthHeaders() {
    return {
      Authorization: `Bearer ${localStorage.getItem('token')}`,
    }
  }

  /**
   * Assign multiple conversations to a team member
   */
  async bulkAssign(payload: BulkAssignPayload): Promise<BulkOperationResponse> {
    try {
      const response = await axios.post(
        `${API_URL}gateway/inbox/bulk-assign`,
        payload,
        { headers: this.getAuthHeaders() }
      )
      return response.data
    } catch (error: any) {
      throw new Error(error?.response?.data?.message || 'Failed to assign conversations')
    }
  }

  /**
   * Archive multiple conversations
   */
  async bulkArchive(payload: BulkOperationPayload): Promise<BulkOperationResponse> {
    try {
      const response = await axios.post(
        `${API_URL}gateway/inbox/bulk-archive`,
        payload,
        { headers: this.getAuthHeaders() }
      )
      return response.data
    } catch (error: any) {
      throw new Error(error?.response?.data?.message || 'Failed to archive conversations')
    }
  }

  /**
   * Unsubscribe multiple conversations
   */
  async bulkUnsubscribe(payload: BulkOperationPayload): Promise<BulkOperationResponse> {
    try {
      const response = await axios.post(
        `${API_URL}gateway/inbox/bulk-unsubscribe`,
        payload,
        { headers: this.getAuthHeaders() }
      )
      return response.data
    } catch (error: any) {
      throw new Error(error?.response?.data?.message || 'Failed to unsubscribe conversations')
    }
  }

  /**
   * Add tags to multiple conversations
   */
  async bulkAddTags(payload: BulkTagPayload): Promise<BulkOperationResponse> {
    try {
      const response = await axios.post(
        `${API_URL}gateway/inbox/bulk-add-tags`,
        payload,
        { headers: this.getAuthHeaders() }
      )
      return response.data
    } catch (error: any) {
      throw new Error(error?.response?.data?.message || 'Failed to add tags to conversations')
    }
  }

  /**
   * Remove tags from multiple conversations
   */
  async bulkRemoveTags(payload: BulkTagPayload): Promise<BulkOperationResponse> {
    try {
      const response = await axios.post(
        `${API_URL}gateway/inbox/bulk-remove-tags`,
        payload,
        { headers: this.getAuthHeaders() }
      )
      return response.data
    } catch (error: any) {
      throw new Error(error?.response?.data?.message || 'Failed to remove tags from conversations')
    }
  }

  /**
   * Mark multiple conversations as read
   */
  async bulkMarkAsRead(payload: BulkOperationPayload): Promise<BulkOperationResponse> {
    try {
      const response = await axios.post(
        `${API_URL}gateway/inbox/bulk-read`,
        payload,
        { headers: this.getAuthHeaders() }
      )
      return response.data
    } catch (error: any) {
      throw new Error(error?.response?.data?.message || 'Failed to mark conversations as read')
    }
  }

  /**
   * Mark multiple conversations as unread
   */
  async bulkMarkAsUnread(payload: BulkOperationPayload): Promise<BulkOperationResponse> {
    try {
      const response = await axios.post(
        `${API_URL}gateway/inbox/bulk-mark-as-unread`,
        payload,
        { headers: this.getAuthHeaders() }
      )
      return response.data
    } catch (error: any) {
      throw new Error(error?.response?.data?.message || 'Failed to mark conversations as unread')
    }
  }

  /**
   * Close multiple conversations as read
   */
  async bulkCloseAsRead(payload: BulkOperationPayload): Promise<BulkOperationResponse> {
    try {
      const response = await axios.post(
        `${API_URL}gateway/inbox/bulk-close`,
        payload,
        { headers: this.getAuthHeaders() }
      )
      return response.data
    } catch (error: any) {
      throw new Error(error?.response?.data?.message || 'Failed to close conversations as read')
    }
  }

  /**
   * Flag/unflag multiple conversations
   */
  async bulkFlag(payload: BulkFlagPayload): Promise<BulkOperationResponse> {
    try {
      const response = await axios.post(
        `${API_URL}gateway/inbox/bulk-flag`,
        payload,
        { headers: this.getAuthHeaders() }
      )
      return response.data
    } catch (error: any) {
      throw new Error(error?.response?.data?.message || 'Failed to flag conversations')
    }
  }

  /**
   * Delete multiple conversations
   */
  async bulkDelete(payload: BulkOperationPayload): Promise<BulkOperationResponse> {
    try {
      const response = await axios.delete(
        `${API_URL}gateway/inbox/bulk-delete`,
        {
          data: payload,
          headers: this.getAuthHeaders(),
        }
      )
      return response.data
    } catch (error: any) {
      throw new Error(error?.response?.data?.message || 'Failed to delete conversations')
    }
  }

  /**
   * Restore multiple conversations from archive
   */
  async bulkRestore(payload: BulkOperationPayload): Promise<BulkOperationResponse> {
    try {
      const response = await axios.post(
        `${API_URL}gateway/inbox/bulk-restore`,
        payload,
        { headers: this.getAuthHeaders() }
      )
      return response.data
    } catch (error: any) {
      throw new Error(error?.response?.data?.message || 'Failed to restore conversations')
    }
  }

  /**
   * Move multiple conversations to spam
   */
  async bulkMoveToSpam(payload: BulkOperationPayload): Promise<BulkOperationResponse> {
    try {
      const response = await axios.post(
        `${API_URL}gateway/inbox/bulk-move-to-spam`,
        payload,
        { headers: this.getAuthHeaders() }
      )
      return response.data
    } catch (error: any) {
      throw new Error(error?.response?.data?.message || 'Failed to move conversations to spam')
    }
  }

  /**
   * Export multiple conversations
   */
  async bulkExport(payload: BulkOperationPayload & {
    format?: 'csv' | 'xlsx' | 'json'
    includeMetadata?: boolean
  }): Promise<BulkOperationResponse> {
    try {
      const response = await axios.post(
        `${API_URL}gateway/inbox/bulk-export`,
        payload,
        {
          headers: this.getAuthHeaders(),
          responseType: 'blob' // For file downloads
        }
      )
      return response.data
    } catch (error: any) {
      throw new Error(error?.response?.data?.message || 'Failed to export conversations')
    }
  }

  /**
   * Set priority for multiple conversations
   */
  async bulkSetPriority(payload: BulkOperationPayload & {
    priority: 'low' | 'normal' | 'high' | 'urgent'
  }): Promise<BulkOperationResponse> {
    try {
      const response = await axios.post(
        `${API_URL}gateway/inbox/bulk-set-priority`,
        payload,
        { headers: this.getAuthHeaders() }
      )
      return response.data
    } catch (error: any) {
      throw new Error(error?.response?.data?.message || 'Failed to set priority for conversations')
    }
  }

  /**
   * Add notes to multiple conversations
   */
  async bulkAddNote(payload: BulkOperationPayload & {
    note: string
    isPrivate?: boolean
  }): Promise<BulkOperationResponse> {
    try {
      const response = await axios.post(
        `${API_URL}gateway/inbox/bulk-add-note`,
        payload,
        { headers: this.getAuthHeaders() }
      )
      return response.data
    } catch (error: any) {
      throw new Error(error?.response?.data?.message || 'Failed to add notes to conversations')
    }
  }

  /**
   * Change status of multiple conversations
   */
  async bulkChangeStatus(payload: BulkOperationPayload & {
    status: 'open' | 'pending' | 'resolved' | 'closed'
  }): Promise<BulkOperationResponse> {
    try {
      const response = await axios.post(
        `${API_URL}gateway/inbox/bulk-change-status`,
        payload,
        { headers: this.getAuthHeaders() }
      )
      return response.data
    } catch (error: any) {
      throw new Error(error?.response?.data?.message || 'Failed to change status of conversations')
    }
  }

  /**
   * Get bulk operation history/logs
   */
  async getBulkOperationHistory(filters?: {
    dateFrom?: string
    dateTo?: string
    operation?: string
    userId?: string
    limit?: number
    offset?: number
  }): Promise<BulkOperationResponse> {
    try {
      const response = await axios.get(
        `${API_URL}gateway/inbox/bulk-operation-history`,
        {
          params: filters,
          headers: this.getAuthHeaders()
        }
      )
      return response.data
    } catch (error: any) {
      throw new Error(error?.response?.data?.message || 'Failed to fetch bulk operation history')
    }
  }

  /**
   * Get bulk operation progress/status for long-running operations
   */
  async getBulkOperationStatus(operationId: string): Promise<BulkOperationResponse> {
    try {
      const response = await axios.get(
        `${API_URL}gateway/inbox/bulk-operation-status/${operationId}`,
        { headers: this.getAuthHeaders() }
      )
      return response.data
    } catch (error: any) {
      throw new Error(error?.response?.data?.message || 'Failed to fetch bulk operation status')
    }
  }

  /**
   * Cancel a running bulk operation
   */
  async cancelBulkOperation(operationId: string): Promise<BulkOperationResponse> {
    try {
      const response = await axios.post(
        `${API_URL}gateway/inbox/bulk-operation-cancel/${operationId}`,
        {},
        { headers: this.getAuthHeaders() }
      )
      return response.data
    } catch (error: any) {
      throw new Error(error?.response?.data?.message || 'Failed to cancel bulk operation')
    }
  }

  /**
   * Preview bulk operation results before execution
   */
  async previewBulkOperation(operation: string, payload: any): Promise<BulkOperationResponse> {
    try {
      const response = await axios.post(
        `${API_URL}gateway/inbox/bulk-operation-preview`,
        {
          operation,
          ...payload
        },
        { headers: this.getAuthHeaders() }
      )
      return response.data
    } catch (error: any) {
      throw new Error(error?.response?.data?.message || 'Failed to preview bulk operation')
    }
  }
}

// Export singleton instance
export const bulkOperationsAPI = new BulkOperationsAPI()

// Export individual operation types for type safety
export {
  BulkOperationsAPI
}

// Export utility functions
export const bulkOperationUtils = {
  /**
   * Format success message for bulk operations
   */
  formatSuccessMessage: (operation: string, count: number): string => {
    const operationMap: Record<string, string> = {
      assign: 'assigned',
      archive: 'archived',
      unsubscribe: 'unsubscribed',
      addTags: 'tagged',
      removeTags: 'untagged',
      markAsRead: 'marked as read',
      markAsUnread: 'marked as unread',
      closeAsRead: 'closed as read',
      flag: 'flagged',
      unflag: 'unflagged',
      delete: 'deleted',
      restore: 'restored',
      moveToSpam: 'moved to spam',
      setPriority: 'priority updated',
      addNote: 'notes added',
      changeStatus: 'status changed'
    }

    const actionText = operationMap[operation] || operation
    return `${count} conversation${count > 1 ? 's' : ''} ${actionText} successfully`
  },

  /**
   * Validate conversation IDs
   */
  validateConversationIds: (ids: string[]): boolean => {
    return Array.isArray(ids) && ids.length > 0 && ids.every(id => typeof id === 'string' && id.trim().length > 0)
  },

  /**
   * Chunk conversation IDs for batch processing
   */
  chunkConversationIds: (ids: string[], chunkSize: number = 100): string[][] => {
    const chunks: string[][] = []
    for (let i = 0; i < ids.length; i += chunkSize) {
      chunks.push(ids.slice(i, i + chunkSize))
    }
    return chunks
  },

  /**
   * Estimate operation duration based on conversation count
   */
  estimateOperationDuration: (count: number, operationType: string): number => {
    // Base time per conversation in milliseconds
    const baseTimeMap: Record<string, number> = {
      assign: 100,
      archive: 150,
      unsubscribe: 200,
      addTags: 120,
      removeTags: 120,
      markAsRead: 80,
      markAsUnread: 80,
      closeAsRead: 100,
      flag: 80,
      delete: 300,
      export: 500
    }

    const baseTime = baseTimeMap[operationType] || 150
    return count * baseTime
  }
}
